import React, { Suspense, lazy } from 'react';

import GhostTile from '@/components/modular-grid/GhostTile';
import { TileSize } from '@/components/modular-grid/ModularGridGameTile/ModularGridGameTile';
import { GameClickProps, GameTileProps } from '@/components/modular-grid/types';
import { handleClick } from '@/components/modular-grid/utils';
import { CategoryFinancialWebAttributes } from '@/types/category/categoryTypes';

import styles from './PatternGrid.module.scss';

export type SupportedTileSize = Extract<
  TileSize,
  'small' | 'tall-vertical' | 'wide-horizontal'
>;

export interface PatternGridProps {
  pattern?: SupportedTileSize[];
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game: GameClickProps;
    path: string;
  }) => void;
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  categoryAttributes?: CategoryFinancialWebAttributes;
}

const ModularGridGameTile = lazy(() =>
  import(
    /* webpackChunkName: "ModularGridGameTile" */
    '../ModularGridGameTile'
  )
);

export default function PatternGrid({
  pattern = ['small'],
  gameList,
  onClick,
  onInfoClick,
  gameDetailsInfo,
  gameInfoTooltip,
  categoryAttributes,
}: PatternGridProps) {
  if (!gameList) return null;

  return (
    <div className={styles.layout}>
      {gameList?.map((item, index) => {
        const tileSize: SupportedTileSize =
          index < pattern.length ? pattern[index] : 'small';

        if (tileSize === 'small') {
          return (
            <Suspense fallback={<GhostTile />} key={item.id}>
              <ModularGridGameTile
                game={item}
                data-testid="small-tile"
                onClick={handleClick(index, onClick)}
                onInfoClick={handleClick(index, onInfoClick)}
                outfit={'medium'}
                gameDetailsInfo={gameDetailsInfo}
                gameInfoTooltip={gameInfoTooltip}
                isLazy
                isAnchor
                categoryAttributes={categoryAttributes}
                tileSize={tileSize}
              />
            </Suspense>
          );
        }

        return (
          <div
            className={
              tileSize === 'tall-vertical' ? styles.vertical : styles.horizontal
            }
            key={item.id}
          >
            <Suspense fallback={<GhostTile />}>
              <ModularGridGameTile
                game={item}
                data-testid={
                  tileSize === 'tall-vertical'
                    ? 'vertical-tile'
                    : 'horizontal-tile'
                }
                onClick={handleClick(index, onClick)}
                onInfoClick={handleClick(index, onInfoClick)}
                outfit={'medium'}
                gameDetailsInfo={gameDetailsInfo}
                gameInfoTooltip={gameInfoTooltip}
                isLazy
                isAnchor
                categoryAttributes={categoryAttributes}
                tileSize={tileSize}
              />
            </Suspense>
          </div>
        );
      })}
    </div>
  );
}
